import { Deal, DealDTO, DealStatusTypes, DealType } from 'modules/domain/deal/types'
import { useTranslation } from 'react-i18next'
import { isRussia } from 'env'
import { useFormik } from 'formik'
import { SharedModalContent, SharedModalInputs } from 'views/components/SharedModal'
import { RequestError } from 'modules/errors'
import { apiClient } from 'modules/utils/httpClient'
import { endpoints } from 'modules/endpoints'
import { ShortFormControl } from 'views/styled/common'
import FormSelectShared from 'views/components/FormSelectShared'
import { QuantityInput } from 'views/components/QuantityInput/QuantityInput'
import { useAFormHandler } from 'analytics/hooks'
import { AnalyticsPlaces } from '../../analyticsPlaces'
import { labelDistance } from 'modules/utils/numbers/formatDistance'
import { labelPriceLogistics } from 'modules/utils/numbers/formatPriceLogistics'
import { FC } from 'react'
import { PriceCarrierInput } from './PriceCarrierInput'
import { FormInput } from 'views/components/form/FormInput'
import { refetchFunc } from 'modules/domain/common/hooks'
import styled from 'styled-components'
import { PricesControl } from './PricesControl'
import { BrokerType } from 'modules/domain/user/types'
import { changeErrorsDotsToPipes } from 'modules/utils/helpers'
import { DealEditableFields } from './types'
import { ResponsiveModal } from 'views/components/ResponsiveModal/ResponsiveModal'
import { defaultTheme } from '@agro-club/agroclub-shared'
import { MarginControl } from './MarginControl'
import { isStatusAfter } from '../helpers'
import { usePermissions } from 'views/components/PermissionCheck/usePermissions'

type Props = {
  deal: Deal
  refetch: refetchFunc
  onClose: () => void
}

const ShortLabelWrapper = styled.div`
  max-width: 210px;
  @media (max-width: ${defaultTheme.queries.mobile}) {
    max-width: none;
  }
`

const fillBrokerFields = (values: DealEditableFields) => {
  const {
    'seller_broker|broker': seller_broker_broker,
    'seller_broker|commission': seller_broker_commission,
    'customer_broker|broker': customer_broker_broker,
    'customer_broker|commission': customer_broker_commission,
    ...rest
  } = values
  let seller_broker: Record<string, unknown> | null = null
  let customer_broker: Record<string, unknown> | null = null

  if (seller_broker_broker && (isRussia || rest.deal_type === DealType.broker)) {
    seller_broker = {
      broker: seller_broker_broker,
      commission: seller_broker_commission,
      broker_type: BrokerType.seller,
    }
  }
  if (customer_broker_broker && rest.deal_type === DealType.broker) {
    customer_broker = {
      broker: customer_broker_broker,
      commission: customer_broker_commission,
      broker_type: BrokerType.customer,
    }
  }

  return { seller_broker, customer_broker, rest }
}
export const DealDetailEditModal: FC<Props> = ({ deal, onClose, refetch }) => {
  const { formProgress, formHandler } = useAFormHandler('editDeal', { id: deal.id })
  const { t } = useTranslation(['deal', 'executors', 'bid'])

  const canEditPrices = usePermissions(['deals.can_change_deal_extra'])
  const canEditLogistics = usePermissions(['deals.can_change_logistics_fields'])
  const priceEditDisabled = isStatusAfter(deal.status, DealStatusTypes.AGREED) && !canEditPrices
  const logisticEditDisabled = !canEditLogistics

  const formik = useFormik<DealEditableFields>({
    initialValues: {
      deal_type: deal.deal_type,
      quantity: deal.quantity,
      distance: deal.distance,
      sale_price: deal.sale_price,
      purchase_price: deal.purchase_price,
      price_logistics: deal.price_logistics,
      price_logistics_with_overload: deal.price_logistics_with_overload,
      price_carrier: deal.price_carrier,
      fixed_margin: deal.fixed_margin ?? 0,
      payment_delay_days: deal.payment_delay_days ?? null,
      railway_price_logistics: deal.railway_price_logistics ?? null,

      'seller_broker|broker': deal.seller_broker ? deal.seller_broker.broker.id : null,
      'seller_broker|commission': deal.seller_broker ? deal.seller_broker.commission : null,
      'customer_broker|broker': deal.customer_broker ? deal.customer_broker.broker.id : null,
      'customer_broker|commission': deal.customer_broker ? deal.customer_broker.commission : null,
      fixed_margin_by_export_contract: deal.fixed_margin_by_export_contract,
    },
    enableReinitialize: true,
    onSubmit: formHandler(
      async () => {
        const values = fillBrokerFields(formik.values)
        const { sale_price, purchase_price, price_logistics, price_logistics_with_overload, price_carrier, ...rest } =
          values.rest
        const dto = {
          ...rest,
          ...(priceEditDisabled ? {} : { sale_price, purchase_price }),
          ...(logisticEditDisabled ? {} : { price_logistics, price_logistics_with_overload, price_carrier }),
          seller_broker: values.seller_broker,
          customer_broker: values.customer_broker,
        }
        await apiClient.patch<DealDTO>(endpoints.deal(deal.id), dto)
      },
      {
        onSuccess: async () => {
          await refetch()
          onClose()
        },
        onError: (err) => {
          const error = err as RequestError
          let { errors } = error
          errors = changeErrorsDotsToPipes(errors)
          formik.setErrors(errors)
        },
      },
    ),
  })

  const isDisabledFixedMargin = formik.values.deal_type === DealType.trade
  return (
    <ResponsiveModal
      onConfirm={() => formik.submitForm()}
      id={AnalyticsPlaces.EDIT_DETAIL_MODAL}
      confirmButtonText={t('common:save')}
      disabled={!formik.dirty}
      title={t('dealDetail')}
      progress={formProgress}
      onClose={onClose}
      size="medium"
    >
      <SharedModalContent>
        <SharedModalInputs>
          <ShortFormControl>
            <FormSelectShared
              label={t('fields.deal_type')}
              formik={formik}
              fieldName="deal_type"
              getLabel={(dto) => dto.label}
              endpoint={endpoints.dealTypes()}
              placeholder=""
              simpleApiFormat
              showSearch={false}
              allowClear={false}
            />
          </ShortFormControl>
          <PricesControl formik={formik} deal={deal} priceEditDisabled={priceEditDisabled} />
          <ShortFormControl>
            <QuantityInput formik={formik} fieldName="quantity" label={t('common:volume')} />
            <FormInput formik={formik} field="distance" label={labelDistance(t('fields.distance'))} type="number" />
            {formik.values.deal_type !== DealType.railway && (
              <>
                <FormInput
                  formik={formik}
                  disabled={logisticEditDisabled}
                  field="price_logistics"
                  label={<ShortLabelWrapper>{labelPriceLogistics(t('fields.price_logistics'))}</ShortLabelWrapper>}
                  type="number"
                />
                <FormInput
                  formik={formik}
                  disabled={logisticEditDisabled}
                  field="price_logistics_with_overload"
                  label={
                    <ShortLabelWrapper>
                      {labelPriceLogistics(t('fields.price_logistics_with_overload'))}
                    </ShortLabelWrapper>
                  }
                  type="number"
                />
                <PriceCarrierInput formik={formik} logisticEditDisabled={logisticEditDisabled} />
              </>
            )}
            {formik.values.deal_type === DealType.railway && (
              <FormInput
                formik={formik}
                field="railway_price_logistics"
                label={
                  <ShortLabelWrapper>{labelPriceLogistics(t('fields.railway_price_logistics'))}</ShortLabelWrapper>
                }
                type="number"
              />
            )}
            <MarginControl formik={formik} disabled={isDisabledFixedMargin} deal={deal} />

            {isRussia && (
              <FormInput
                label={`${t('fields.paymentDelayForInput')}`}
                field="payment_delay_days"
                formik={formik}
                type="number"
              />
            )}
          </ShortFormControl>
        </SharedModalInputs>
      </SharedModalContent>
    </ResponsiveModal>
  )
}
